import {Outlet, useNavigate, useSearchParams, useLocation} from 'react-router-dom'
import {useIntl} from 'react-intl'
import {useState, useRef, useEffect, useCallback} from 'react'
import {Link} from 'react-router-dom'
import {PublicWrapper} from '../../modules/common/components/PublicWrapper'
import {StartFreeToday} from '../landing/components/StartFreeToday'
import {KTSVG} from '../../../_gori/helpers/components/KTSVG'
import {ROUTES} from '../../../_gori/constants'
import {sanitizeHTML} from './utils/sanitize'
import {SeoHead} from '../../modules/seo'

type BlogPost = {
  id: number
  title: string
  slug: string
  featured_image: string | null
}

export function BlogLayout() {
  const intl = useIntl()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const searchWrapperRef = useRef<HTMLDivElement>(null)
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '')
  const [previewResults, setPreviewResults] = useState<BlogPost[]>([])
  const [previewLoading, setPreviewLoading] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [isSearchFocused, setIsSearchFocused] = useState(false)

  const isDetailPage =
    location.pathname.startsWith(`${ROUTES.RESOURCE.BLOG}/`) &&
    location.pathname !== ROUTES.RESOURCE.BLOG
  const hasActiveSearch = searchParams.get('search')

  useEffect(() => {
    if (isDetailPage) {
      setSearchQuery('')
    } else {
      setSearchQuery(searchParams.get('search') || '')
    }
  }, [location.pathname, searchParams, isDetailPage])

  const handleSearchSubmit = useCallback(() => {
    if (searchQuery.trim()) {
      navigate(`${ROUTES.RESOURCE.BLOG}?search=${encodeURIComponent(searchQuery)}`)
    } else {
      navigate(ROUTES.RESOURCE.BLOG)
    }
    setShowPreview(false)
    setIsSearchFocused(false)
  }, [searchQuery, navigate])

  const fetchPreview = useCallback(async () => {
    if (!searchQuery || searchQuery.length < 2 || !isSearchFocused) {
      setPreviewResults([])
      setShowPreview(false)
      return
    }

    setPreviewLoading(true)
    setShowPreview(true)

    try {
      const apiUrl = process.env.REACT_APP_API_URL || ''
      const res = await fetch(
        `${apiUrl}/api/blog/posts?page=1&per_page=5&search=${encodeURIComponent(searchQuery)}`
      )

      if (!res.ok) throw new Error('Failed to fetch preview')

      const data = await res.json()
      setPreviewResults(data.posts || [])
    } catch (err) {
      console.error('Error fetching preview:', err)
      setPreviewResults([])
    } finally {
      setPreviewLoading(false)
    }
  }, [searchQuery, isSearchFocused])

  useEffect(() => {
    const timer = setTimeout(fetchPreview, 300)
    return () => clearTimeout(timer)
  }, [fetchPreview])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(event.target as Node)) {
        setShowPreview(false)
        setIsSearchFocused(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <PublicWrapper>
      {!isDetailPage && <SeoHead />}
      <section className='blog-header'>
        <div className='blog-header-container'>
          {isDetailPage || hasActiveSearch ? (
            <h1
              className='blog-header-title blog-header-title-clickable'
              onClick={() => {
                setSearchQuery('')
                navigate(ROUTES.RESOURCE.BLOG)
              }}
            >
              <KTSVG
                path='/media/gori/left-chevron-white.svg'
                className='me-2'
                svgClassName='h-20px w-20px'
              />
              {intl.formatMessage({id: 'BLOG_TITLE'})}
            </h1>
          ) : (
            <h1 className='blog-header-title'>{intl.formatMessage({id: 'BLOG_TITLE'})}</h1>
          )}
          <div className='blog-search-wrapper' ref={searchWrapperRef}>
            <i className='bi bi-search blog-search-icon'></i>
            <input
              type='text'
              className='form-control blog-search-input'
              placeholder={intl.formatMessage({id: 'SEARCH_BLOG'})}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setIsSearchFocused(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearchSubmit()
                }
              }}
            />
            {searchQuery && (
              <button
                className='blog-search-clear'
                onClick={() => {
                  setSearchQuery('')
                  setShowPreview(false)
                  navigate(ROUTES.RESOURCE.BLOG)
                }}
                aria-label='Clear search'
              >
                <i className='bi bi-x'></i>
              </button>
            )}
            {showPreview && searchQuery && (
              <div className='blog-search-preview'>
                {previewLoading ? (
                  <div className='blog-search-preview-loading'>
                    <div className='spinner-border spinner-border-sm' role='status'>
                      <span className='visually-hidden'>Loading...</span>
                    </div>
                  </div>
                ) : previewResults.length > 0 ? (
                  <>
                    {previewResults.slice(0, 5).map((post) => (
                      <Link
                        key={post.id}
                        to={`${ROUTES.RESOURCE.BLOG}/${post.slug}`}
                        className='blog-search-preview-item'
                        onClick={() => {
                          setShowPreview(false)
                          setIsSearchFocused(false)
                        }}
                      >
                        {post.featured_image && (
                          <img
                            src={post.featured_image}
                            alt={post.title}
                            className='blog-search-preview-image'
                          />
                        )}
                        <div className='blog-search-preview-title'>
                          {sanitizeHTML(post.title, false)}
                        </div>
                      </Link>
                    ))}
                  </>
                ) : (
                  <div className='blog-search-preview-empty'>
                    {intl.formatMessage({id: 'NO_RESULTS_FOUND'})}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      <div className='blog-content-wrapper'>
        <Outlet />
        <StartFreeToday />
      </div>
    </PublicWrapper>
  )
}
